import { useEffect, useState } from "react";
import api from "../lib/api";
import { Panel } from "../components/common";
import { regimeColor, chgColor, fmt } from "../lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Globe, Activity, Landmark, Flag } from "lucide-react";

const FLAG = { EA: "🇪🇺", DE: "🇩🇪", FR: "🇫🇷", IT: "🇮🇹", ES: "🇪🇸", GB: "🇬🇧", US: "🇺🇸" };

function RegimeCard({ r, flag, region }) {
  if (!r) return null;
  const color = regimeColor(r.regime);
  return (
    <div className="panel p-5 fade-up" data-testid={`regime-card-${region}`} style={{ borderColor: `${color}44` }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{flag}</span>
          <div>
            <div className="eyebrow">{r.region_name || (region === "us" ? "United States" : region)}</div>
            <div className="font-display text-2xl font-extrabold" style={{ color }}>{r.label}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="eyebrow">confidence</div>
          <div className="font-mono text-2xl font-bold tnum" style={{ color }}>{r.confidence}%</div>
        </div>
      </div>
      <div className="flex gap-4 mt-3 text-xs font-mono">
        <span>Growth <b style={{ color: r.growth_direction === "rising" ? "#10B981" : "#EF4444" }}>{r.growth_direction}</b></span>
        <span>Inflation <b style={{ color: r.inflation_direction === "rising" ? "#EF4444" : "#10B981" }}>{r.inflation_direction}</b></span>
      </div>
      <div className="mt-4 space-y-2">
        {Object.entries(r.factors || {}).map(([k, f]) => (
          <div key={k} data-testid={`regime-factor-${region}-${k}`}>
            <div className="flex items-center justify-between text-[11px] font-mono mb-0.5">
              <span className="text-slate-400 capitalize">{k} <span className="text-slate-600">· {f.direction}</span></span>
              <span className="tnum text-slate-300">z {f.z}</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#1B243B] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${f.score}%`, background: color }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GlobalMacro() {
  const [d, setD] = useState(null);
  const [dash, setDash] = useState(null);
  const [region, setRegion] = useState("US");

  useEffect(() => {
    api.get("/global-macro").then((r) => setD(r.data)).catch(() => {});
    api.get("/macro-dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="font-mono text-sm text-slate-500 animate-pulse">loading global macro…</div>;

  const cpiSeries = (d.series?.us?.cpi || []).map((p, i) => ({
    t: p.t, US: p.v, EA: d.series?.ea?.cpi?.[i]?.v ?? null,
  }));
  const rateSeries = (d.series?.us?.policy || []).map((p, i) => ({
    t: p.t, US: p.v, EA: d.series?.ea?.policy?.[i]?.v ?? null,
  }));

  return (
    <div className="space-y-5" data-testid="global-macro-page">
      <div>
        <div className="eyebrow">macro engine · cross-region</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Globe size={26} className="text-[#00E5FF]" /> Global Macro — US vs Eurozone
        </h1>
      </div>

      {/* regime cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="regime-comparison">
        <RegimeCard r={d.us_regime} flag={FLAG.US} region="us" />
        <RegimeCard r={d.eu_regime} flag={FLAG.EA} region="ea" />
      </div>

      {/* headline comparison */}
      <Panel title="Headline Indicators — United States vs Eurozone" icon={<Activity size={13} />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="macro-comparison-grid">
          {d.comparison.map((c) => {
            const usWins = c.lower_better ? c.us < c.ea : c.us > c.ea;
            return (
              <div key={c.indicator} className="panel-2 rounded-md p-3">
                <div className="eyebrow mb-2">{c.indicator}</div>
                <div className="space-y-2">
                  {[["US", c.us, usWins], ["EA", c.ea, !usWins]].map(([lbl, val, lead]) => (
                    <div key={lbl} className="flex items-center gap-2">
                      <span className="text-xs">{FLAG[lbl === "US" ? "US" : "EA"]}</span>
                      <span className="font-mono text-[11px] w-6 text-slate-400">{lbl}</span>
                      <div className="flex-1 h-2 rounded-full bg-[#1B243B] overflow-hidden">
                        <div className="h-full rounded-full" style={{
                          width: `${Math.min(100, Math.abs(val) / Math.max(Math.abs(c.us), Math.abs(c.ea), 0.1) * 100)}%`,
                          background: lead ? "#00E5FF" : "#475569",
                        }} />
                      </div>
                      <span className="font-mono text-xs font-bold tnum w-12 text-right" style={{ color: lead ? "#00E5FF" : "#CBD5E1" }}>
                        {fmt(val, c.unit === "%" ? 1 : 2)}{c.unit}
                      </span>
                    </div>
                  ))}
                </div>
                {c.note && <div className="mt-2 text-[9px] font-mono text-slate-600">{c.note}</div>}
              </div>
            );
          })}
        </div>
      </Panel>

      {/* trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Inflation Path — CPI/HICP YoY" icon={<Activity size={13} />}>
          <TrendChart data={cpiSeries} unit="%" />
        </Panel>
        <Panel title="Policy Rate — Fed Funds vs ECB" icon={<Landmark size={13} />}>
          <TrendChart data={rateSeries} unit="%" />
        </Panel>
      </div>

      {/* country table */}
      <Panel title="European Country Snapshot" icon={<Flag size={13} />}
        right={<span className="text-[10px] font-mono text-slate-500">IP = modeled · rest = live FRED</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="eu-country-table">
            <thead>
              <tr className="eyebrow border-b border-[#1E293B]">
                <th className="py-2 pr-4">Country</th>
                <th className="py-2 px-3 text-right">Inflation</th>
                <th className="py-2 px-3 text-right">GDP YoY</th>
                <th className="py-2 px-3 text-right">Unemployment</th>
                <th className="py-2 px-3 text-right">Ind. Prod.*</th>
                <th className="py-2 px-3 text-right">Policy Rate</th>
                <th className="py-2 pl-3 text-right">Source</th>
              </tr>
            </thead>
            <tbody>
              {d.countries.map((c) => (
                <tr key={c.code} data-testid={`country-row-${c.code}`} className="border-b border-[#141C2E] hover:bg-[#141C2E] transition-colors">
                  <td className="py-2.5 pr-4 font-mono font-bold"><span className="mr-2">{FLAG[c.code]}</span>{c.name}</td>
                  <td className="py-2.5 px-3 text-right font-mono tnum" style={{ color: c.cpi > 3 ? "#F59E0B" : "#CBD5E1" }}>{fmt(c.cpi, 1)}%</td>
                  <td className="py-2.5 px-3 text-right font-mono tnum" style={{ color: chgColor(c.gdp) }}>{fmt(c.gdp, 1)}%</td>
                  <td className="py-2.5 px-3 text-right font-mono tnum" style={{ color: c.unemployment > 8 ? "#EF4444" : "#CBD5E1" }}>{fmt(c.unemployment, 1)}%</td>
                  <td className="py-2.5 px-3 text-right font-mono tnum" style={{ color: chgColor(c.ip) }}>{fmt(c.ip, 1)}%</td>
                  <td className="py-2.5 px-3 text-right font-mono tnum">{fmt(c.policy_rate, 2)}%</td>
                  <td className="py-2.5 pl-3 text-right">
                    <span className="text-[9px] font-mono px-2 py-0.5 rounded border"
                      style={c.source === "live"
                        ? { color: "#10B981", borderColor: "#10B98144", background: "#10B98112" }
                        : { color: "#64748B", borderColor: "#33415544" }}>
                      {c.source === "live" ? "● LIVE" : "○ modeled"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 eyebrow">* industrial production is modeled — FRED no longer publishes fresh EU IP series on the free tier</div>
      </Panel>

      {/* detailed per-region macro drilldown */}
      {dash && <MacroDrilldown dash={dash} region={region} setRegion={setRegion} />}
    </div>
  );
}

function IndicatorCard({ ind }) {
  const rising = ind.trend === "rising";
  const col = rising ? "#10B981" : "#EF4444";
  return (
    <div className="panel-2 rounded-md p-3" data-testid={`macro-ind-${ind.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-slate-400 truncate">{ind.name}</div>
          <div className="font-mono text-2xl font-extrabold tnum mt-0.5">{fmt(ind.value, ind.unit === "idx" ? 0 : 1)}<span className="text-sm text-slate-500 ml-0.5">{ind.unit === "%" ? "%" : ""}</span></div>
        </div>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border shrink-0"
          style={ind.source === "live"
            ? { color: "#10B981", borderColor: "#10B98144", background: "#10B98112" }
            : { color: "#64748B", borderColor: "#33415544" }}>
          {ind.source === "live" ? "● LIVE" : "○ mdl"}
        </span>
      </div>
      <div className="h-10 my-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={ind.history} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
            <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 11, fontFamily: "JetBrains Mono" }}
              labelFormatter={(l) => l} formatter={(v) => [v, ind.name]} />
            <Line type="monotone" dataKey="v" stroke={col} strokeWidth={1.6} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-slate-500">3m Δ <span style={{ color: chgColor(ind.change_3m) }}>{ind.change_3m > 0 ? "+" : ""}{fmt(ind.change_3m, 1)}</span></span>
        <span className="text-slate-500">pctl {fmt(ind.percentile, 0)}</span>
      </div>
    </div>
  );
}

function MacroDrilldown({ dash, region, setRegion }) {
  const reg = dash.regions.find((r) => r.code === region) || dash.regions[0];
  return (
    <Panel title="Macro Detail — per-region drilldown with history" icon={<Landmark size={13} />}>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="macro-region-tabs">
        {dash.regions.map((r) => (
          <button key={r.code} data-testid={`macro-region-${r.code}`} onClick={() => setRegion(r.code)}
            className={`text-xs font-mono px-3 py-1.5 rounded-md border transition-colors flex items-center gap-1.5 ${reg.code === r.code ? "bg-[#00E5FF12] text-[#00E5FF] border-[#00E5FF44]" : "text-slate-400 border-[#334155] hover:bg-[#141C2E]"}`}>
            <span>{r.flag}</span> {r.name}
          </button>
        ))}
      </div>
      {reg.regime && (
        <div className="mb-4 text-xs font-mono text-slate-400">
          Regime: <b style={{ color: regimeColor(reg.regime.regime) }}>{reg.regime.label}</b> · confidence {reg.regime.confidence}%
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="macro-indicator-grid">
        {reg.indicators.map((ind) => <IndicatorCard key={ind.id} ind={ind} />)}
      </div>
    </Panel>
  );
}

function TrendChart({ data, unit }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
        <CartesianGrid stroke="#1B243B" vertical={false} />
        <XAxis dataKey="t" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} minTickGap={50} />
        <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} unit={unit} />
        <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" }} />
        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
        <Line type="monotone" dataKey="US" stroke="#00E5FF" strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="EA" stroke="#F59E0B" strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
